import * as XLSX from "xlsx";
import { shouldShowXAxisYear } from "../modules/chart-constants";
import { availableSystems, getLevelConfig } from "../modules/emissions/data-config";
import { EMISSIONS_LINE_LABELS, EMISSIONS_UNIT } from "../modules/emissions/emissions-chart-utils";
import { getEnovaActivityDataForSelection } from "../modules/enova-activity-utils";
import { getMarketDataForSelection, MARKET_LINE_LABELS } from "../modules/market/market-chart-utils";

export const toCSV = (rows: (string | number)[][]): string =>
    rows
        .map((row) =>
            row
                .map((val) => {
                    const str = String(val ?? "");
                    return str.includes(",") || str.includes('"') || str.includes("\n")
                        ? `"${str.replace(/"/g, '""')}"`
                        : str;
                })
                .join(","),
        )
        .join("\n");

export const triggerDownload = (content: string, filename: string) => {
    const blob = new Blob(["﻿", content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

const iterateNivel2Items = (
    callback: (system: string, nivel1Key: string, nivel2Item: { key: string; label: string }) => void,
) => {
    for (const system of availableSystems) {
        const level2Config = getLevelConfig(2, { level1: system, level2: null, level3: null });
        for (const nivel1Item of level2Config.navItems) {
            const nivel1Key = nivel1Item.key;
            const level3Config = getLevelConfig(3, { level1: system, level2: nivel1Key, level3: null });
            for (const nivel2Item of level3Config.navItems) {
                callback(system, nivel1Key, nivel2Item);
            }
        }
    }
};

export const buildEmissionsRows = (): (string | number)[][] => {
    const level1Config = getLevelConfig(1, { level1: null, level2: null, level3: null });
    const visibleYears = level1Config.data.map((d) => d.year).filter(shouldShowXAxisYear);

    const rows: (string | number)[][] = [
        [
            "System",
            "Kategori",
            "Underkategori",
            "År",
            `${EMISSIONS_LINE_LABELS.medEnova} (${EMISSIONS_UNIT})`,
            `${EMISSIONS_LINE_LABELS.utenEnova} (${EMISSIONS_UNIT})`,
            `${EMISSIONS_LINE_LABELS.klimamal} (${EMISSIONS_UNIT})`,
        ],
    ];

    for (const year of visibleYears) {
        const d = level1Config.data.find((d) => d.year === year);
        if (!d) continue;
        const total = level1Config.navItems.reduce((sum, item) => sum + (d[item.area] ?? 0), 0);
        rows.push(["Totalt", "", "", year, total, d.utenEnova, d.klimamal]);
    }

    for (const system of availableSystems) {
        const level2Config = getLevelConfig(2, { level1: system, level2: null, level3: null });

        for (const year of visibleYears) {
            const d2 = level2Config.data.find((d) => d.year === year);
            if (!d2) continue;
            const systemTotal = level2Config.navItems.reduce((sum, item) => sum + (d2[item.area] ?? 0), 0);
            rows.push([system, "", "", year, systemTotal, d2.utenEnova, ""]);
        }

        for (const nivel1Item of level2Config.navItems) {
            const nivel1Key = nivel1Item.key;
            const level3Config = getLevelConfig(3, { level1: system, level2: nivel1Key, level3: null });

            for (const year of visibleYears) {
                const d2 = level2Config.data.find((d) => d.year === year);
                const d3 = level3Config.data.find((d) => d.year === year);
                if (!d2) continue;
                rows.push([system, nivel1Key, "", year, d2[nivel1Item.area], d3?.utenEnova ?? "", ""]);
            }

            for (const nivel2Item of level3Config.navItems) {
                const level4Config = getLevelConfig(4, {
                    level1: system,
                    level2: nivel1Key,
                    level3: nivel2Item.key,
                    level4: null,
                });

                for (const year of visibleYears) {
                    const d4 = level4Config.data.find((d) => d.year === year);
                    if (!d4) continue;
                    rows.push([system, nivel1Key, nivel2Item.label, year, d4.area1, d4.utenEnova, ""]);
                }
            }
        }
    }

    return rows;
};

export const buildMarketRows = (): (string | number)[][] => {
    const level1Config = getLevelConfig(1, { level1: null, level2: null, level3: null });
    const visibleYears = level1Config.data.map((d) => d.year).filter(shouldShowXAxisYear);

    const rows: (string | number)[][] = [
        [
            "System",
            "Kategori",
            "Underkategori",
            "År",
            MARKET_LINE_LABELS.totalbestand,
            MARKET_LINE_LABELS.withEnova,
            MARKET_LINE_LABELS.withoutEnova,
        ],
    ];

    iterateNivel2Items((system, nivel1Key, nivel2Item) => {
        const marketData = getMarketDataForSelection({
            level1: system,
            level2: nivel1Key,
            level3: nivel2Item.key,
            level4: null,
        });
        if (!marketData) return;

        for (const year of visibleYears) {
            const d = marketData.find((d) => d.year === year);
            if (!d) continue;
            rows.push([
                system,
                nivel1Key,
                nivel2Item.label,
                year,
                Math.round(d.totalbestand),
                Math.round(d.withEnova),
                Math.round(d.withoutEnova),
            ]);
        }
    });

    return rows;
};

export const buildEnovaAccumulatedRows = (): (string | number)[][] => {
    const rows: (string | number)[][] = [
        ["System", "Kategori", "Underkategori", "År", "Støttet dette året", "Støttet tidligere år", "Totalt støttet"],
    ];

    iterateNivel2Items((system, nivel1Key, nivel2Item) => {
        const activityData = getEnovaActivityDataForSelection({
            level1: system,
            level2: nivel1Key,
            level3: nivel2Item.key,
            level4: null,
        });
        if (!activityData || activityData.length === 0) return;

        let accumulated = 0;
        for (const { year, count } of [...activityData].sort((a, b) => a.year - b.year)) {
            const current = Number.isFinite(count) ? count : 0;
            const previous = accumulated;
            accumulated += current;
            rows.push([system, nivel1Key, nivel2Item.label, year, current, previous, accumulated]);
        }
    });

    return rows;
};

export const buildEnovaYearlyRows = (): (string | number)[][] => {
    const rows: (string | number)[][] = [
        ["System", "Kategori", "Underkategori", "År", "Antall støttet", "Beløp støttet (NOK 1000)"],
    ];

    iterateNivel2Items((system, nivel1Key, nivel2Item) => {
        const activityData = getEnovaActivityDataForSelection({
            level1: system,
            level2: nivel1Key,
            level3: nivel2Item.key,
            level4: null,
        });
        if (!activityData || activityData.length === 0) return;

        for (const { year, count, amount } of [...activityData].sort((a, b) => a.year - b.year)) {
            rows.push([system, nivel1Key, nivel2Item.label, year, count, Math.round(amount / 1000)]);
        }
    });

    return rows;
};

export const buildCsvRows = (): (string | number)[][] => {
    return [
        ["Utslipp"],
        ...buildEmissionsRows(),
        [],
        ["Markedsutvikling"],
        ...buildMarketRows(),
        [],
        ["Enovas aktivitet, samlet"],
        ...buildEnovaAccumulatedRows(),
        [],
        ["Enovas aktivitet, år for år"],
        ...buildEnovaYearlyRows(),
    ];
};

export const triggerXlsxDownload = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildEmissionsRows()), "Utslipp");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildMarketRows()), "Markedsutvikling");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildEnovaAccumulatedRows()), "Enovas aktivitet, samlet");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildEnovaYearlyRows()), "Enovas aktivitet, år for år");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "Klimaomstilling.xlsx";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};
